/* useKeyboardInset — track the iOS Safari soft-keyboard so a chat
 * composer can slide above it instead of being covered.
 *
 * Returns the number of pixels the keyboard is occupying at the bottom
 * of the layout. 0 when no keyboard is open. Drives an inline
 * `bottom: ${inset}px` on the composer container.
 *
 * Why JS? iOS doesn't fire any CSS-only signal when the keyboard
 * opens — `position: sticky` and `bottom: 0` both stop tracking the
 * viewport once the keyboard is up. The visualViewport API is the
 * only reliable cross-browser source of truth.
 *
 * Cleanup: removes both `resize` and `scroll` listeners on unmount
 * (the keyboard fires `scroll` on the visualViewport on iOS).
 *
 * IMPORTANT — perf: the visualViewport.scroll event fires on EVERY
 * iOS Safari URL-bar collapse/expand during normal page scroll, even
 * when no keyboard is open. Without protection, that would force a
 * React re-render of every consumer (BottomNav, chat composer, etc.)
 * on each scroll frame — visible to the user as a jitter/shake along
 * the bottom of the screen during scroll (Jay's reported bug).
 *
 * Two guards added to keep the hook quiet during regular scroll:
 *   1) Subpixel changes <8px are ignored (URL-bar wobble is typically
 *      ~1-3px). The keyboard's real footprint is hundreds of px so we
 *      never miss a real open/close event.
 *   2) rAF-coalesce so multiple events in a single frame collapse to
 *      one update.
 */
import { useEffect, useRef, useState } from 'react';

const useKeyboardInset = () => {
  const [inset, setInset] = useState(0);
  const lastRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const compute = () => {
      // window.innerHeight is the full layout viewport; vv.height shrinks
      // to the visible region above the keyboard. Their difference IS
      // the keyboard's vertical footprint (minus any browser chrome
      // that also collapses — `vv.offsetTop` corrects for that).
      const next = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      // Subpixel noise filter: iOS Safari emits 1-3px wobble on each
      // URL-bar transition during regular scroll. Treat anything below
      // the threshold as "no keyboard" so we don't ping React.
      const normalized = next < 8 ? 0 : next;
      if (normalized === lastRef.current) return;
      lastRef.current = normalized;
      setInset(normalized);
    };
    const schedule = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        compute();
      });
    };
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    compute();
    return () => {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return inset;
};

export default useKeyboardInset;
